import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Mascot } from "./Mascot";
import { CaptionPill } from "./CaptionPill";
import { Fragment as Frag } from "./Fragment";
import { TimerRing } from "./TimerRing";
import { AnnotationCanvas } from "./AnnotationCanvas";
import { lesson, defaultMascotForMode, findMcqById, findSubjectiveById, findExampleById } from "../lib/lessonData";
import { findStoryboardEntry, findExampleStoryboardEntry, findMcqStoryboardEntry, findMcqExplanationStoryboardEntry, loadStoredStoryboard } from "../lib/storyboard";
import QuadraticSlide, { isQuadraticRichSlide } from "./quadratic/QuadraticSlide";
import SoundWaveSlide, { isSoundWaveRichSlide } from "./sound/SoundWaveSlide";
import AngleSlide, { isAngleRichSlide } from "./angle/AngleSlide";
import { isCrossOrigin } from "../lib/audioCache";

/**
 * ScaledStage — fixed 1920x1080 logical canvas, CSS-scaled to fit its parent
 * while preserving 16:9 aspect ratio. This guarantees the *exact same* layout
 * on the fullscreen student display and the teacher's smaller preview, so
 * normalized-coordinate annotations always land in the same content position.
 *
 * `pageBackground` (optional) extends the active slide's background into the
 * letterbox region around the 16:9 stage — important for fullscreen on
 * non-16:9 displays where the default ambient gradient looks out of place.
 */
const ScaledStage = ({ children, pageBackground, innerRef }) => {
    const wrapRef = useRef(null);
    const [t, setT] = useState({ scale: 1, x: 0, y: 0 });

    useLayoutEffect(() => {
        const node = wrapRef.current;
        if (!node) return;
        const update = () => {
            const w = node.clientWidth;
            const h = node.clientHeight;
            if (w === 0 || h === 0) return;
            const s = Math.min(w / 1920, h / 1080);
            setT({
                scale: s,
                x: (w - 1920 * s) / 2,
                y: (h - 1080 * s) / 2,
            });
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(node);
        return () => ro.disconnect();
    }, []);

    const isUrl =
        typeof pageBackground === "string" && /^https?:\/\//.test(pageBackground);
    const outerStyle = pageBackground
        ? isUrl
            ? {
                  backgroundImage: `url("${pageBackground}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  transition: "background 0.45s ease",
              }
            : { background: pageBackground, transition: "background 0.45s ease" }
        : undefined;

    return (
        <div
            ref={wrapRef}
            className={
                "absolute inset-0 overflow-hidden " +
                (pageBackground ? "" : "ambient-bg")
            }
            style={outerStyle}
        >
            <div
                ref={innerRef}
                style={{
                    width: 1920,
                    height: 1080,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
                    transformOrigin: "0 0",
                }}
            >
                {children}
            </div>
        </div>
    );
};

/* -------- shared layout helpers (all sizes are LOGICAL pixels at 1920x1080) -------- */
const Stage = ({ children }) => (
    <div className="absolute inset-0 flex">{children}</div>
);

const TopBadge = () => null;
const ProgressDots = () => null;

/* ---- session-ended goodbye screen ---- */
const SessionEndedView = () => (
    <Stage>
        <div className="absolute inset-0 grid grid-cols-12">
            <div className="col-span-7 flex flex-col justify-center pl-24 pr-12">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                >
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFF1E5] border border-[#FFA987]/40 mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C75730]" />
                        <span className="text-xs uppercase tracking-[0.25em] text-[#C75730] font-semibold">
                            Session complete
                        </span>
                    </div>
                    <h1 className="text-[88px] font-semibold tracking-tight leading-[0.95] mb-5">
                        <span className="block text-[#14202a]">Great job,</span>
                        <span className="sparkl-text-gradient">class!</span>
                    </h1>
                    <p className="text-3xl text-[#4a5662] leading-snug font-light max-w-xl mb-10">
                        That wraps up today's lesson on{" "}
                        <span className="font-medium text-[#14202a]">
                            {lesson.title}
                        </span>
                        . See you in the next class.
                    </p>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="flex items-center gap-3"
                >
                    <Mascot state="idle" size="sm" />
                    <p className="text-lg leading-snug font-light text-[#4a5662]">
                        Take a deep breath.{" "}
                        <span className="text-[#2E8E9A] font-medium">
                            Your AI tutor will see you next time.
                        </span>
                    </p>
                </motion.div>
            </div>
            <div className="col-span-5 relative overflow-hidden">
                <motion.div
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0"
                >
                    <img
                        src={lesson.coverImage || "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1600&q=85"}
                        alt={lesson.coverAlt || "Lesson cover"}
                        className="w-full h-full object-cover"
                    />
                </motion.div>
                <div
                    className="absolute inset-y-0 left-0 w-40"
                    style={{
                        background:
                            "linear-gradient(to right, rgba(248,251,253,1) 0%, rgba(248,251,253,0.55) 50%, rgba(248,251,253,0) 100%)",
                    }}
                />
                <div
                    className="absolute inset-x-0 bottom-0 h-32"
                    style={{
                        background:
                            "linear-gradient(to top, rgba(248,251,253,0.55) 0%, rgba(248,251,253,0) 100%)",
                    }}
                />
            </div>
        </div>
    </Stage>
);

/* ---- welcome view shown after session starts but before any slide is selected ---- */
const SessionWelcomeView = () => (
    <Stage>
        <div className="absolute inset-0 grid grid-cols-12 gap-0">
            {/* Left: editorial copy */}
            <div className="col-span-7 flex flex-col justify-center pl-24 pr-12 relative">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                >
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E0F4F4] border border-[#5BC2CC]/30 mb-6">
                        <span className="w-1.5 h-1.5 rounded-full sparkl-gradient animate-pulse" />
                        <span className="text-xs uppercase tracking-[0.25em] text-[#2E8E9A] font-semibold">
                            Today's lesson
                        </span>
                    </div>
                    <h1 className="text-[88px] font-semibold tracking-tight leading-[0.95] mb-5">
                        <span className="block text-[#14202a]">
                            {lesson.title}
                        </span>
                    </h1>
                    <p className="text-3xl text-[#4a5662] leading-snug font-light max-w-xl mb-10">
                        {lesson.subtitle}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.35 }}
                    className="flex items-center gap-3 mb-10"
                >
                    {[
                        { label: "Duration", value: "~25 min" },
                        { label: "Slides", value: `${lesson.slides.length}` },
                        { label: "Sections", value: `${lesson.sections.length}` },
                    ].map((m) => (
                        <div
                            key={m.label}
                            className="px-5 py-3 rounded-2xl bg-white/80 border border-black/[0.04] backdrop-blur-sm"
                        >
                            <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-0.5">
                                {m.label}
                            </p>
                            <p className="text-xl font-semibold text-[#14202a] tabular-nums">
                                {m.value}
                            </p>
                        </div>
                    ))}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.9, delay: 0.6 }}
                    className="flex items-center gap-3 text-[#4a5662]"
                >
                    <Mascot state="idle" size="sm" />
                    <p className="text-lg leading-snug font-light max-w-md">
                        Hi class — your AI tutor is ready.{" "}
                        <span className="text-[#2E8E9A] font-medium">
                            Waiting for the teacher to pick a slide.
                        </span>
                    </p>
                </motion.div>
            </div>

            {/* Right: hero image, full-bleed with a soft mask */}
            <div className="col-span-5 relative overflow-hidden">
                <motion.div
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0"
                >
                    <img
                        src={lesson.coverImage || "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1600&q=85"}
                        alt={lesson.coverAlt || "Lesson cover"}
                        className="w-full h-full object-cover"
                    />
                </motion.div>
                {/* Left edge fade so the photo blends into the editorial space */}
                <div
                    className="absolute inset-y-0 left-0 w-40"
                    style={{
                        background:
                            "linear-gradient(to right, rgba(248,251,253,1) 0%, rgba(248,251,253,0.6) 50%, rgba(248,251,253,0) 100%)",
                    }}
                />
                {/* Top/bottom subtle vignettes */}
                <div
                    className="absolute inset-x-0 top-0 h-24"
                    style={{
                        background:
                            "linear-gradient(to bottom, rgba(248,251,253,0.6) 0%, rgba(248,251,253,0) 100%)",
                    }}
                />
                <div
                    className="absolute inset-x-0 bottom-0 h-24"
                    style={{
                        background:
                            "linear-gradient(to top, rgba(248,251,253,0.5) 0%, rgba(248,251,253,0) 100%)",
                    }}
                />
                {/* Floating sparkle/orb accent on the image */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.6 }}
                    className="absolute"
                    style={{ top: "62%", right: "8%" }}
                >
                    <Mascot state="speaking" size="lg" />
                </motion.div>
            </div>
        </div>
    </Stage>
);

const IntroView = ({ slide, fragmentIndex }) => (
    <Stage>
        <div className="m-auto flex flex-col items-center text-center px-12 max-w-5xl">
            <Mascot state="speaking" size="lg" />
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-12"
            >
                <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-4">
                    Today's lesson
                </p>
                <h1 className="text-7xl md:text-8xl font-semibold tracking-tight leading-[1.02] mb-5">
                    {slide.title}
                </h1>
                <p className="text-3xl text-[#4a5662] font-light max-w-2xl mx-auto">
                    {slide.subtitle}
                </p>
            </motion.div>

            {fragmentIndex >= 1 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="mt-10 inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-[#E0F4F4] border border-[#5BC2CC]/30"
                >
                    <span className="w-2 h-2 rounded-full bg-[#5BC2CC] animate-pulse" />
                    <span className="text-base text-[#14202a] font-medium">
                        Class in session
                    </span>
                </motion.div>
            )}
        </div>
    </Stage>
);

const ConceptTeachingView = ({ slide, fragmentIndex }) => (
    <Stage>
        <div className="m-auto w-full max-w-[1400px] px-16 grid grid-cols-12 gap-12 items-center">
            <div className="col-span-7">
                <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-5">
                    Concept
                </p>
                <h2 className="text-6xl font-semibold tracking-tight leading-[1.05] mb-12">
                    {slide.title}
                </h2>
                <div className="space-y-6">
                    {slide.fragments.map((f, i) => (
                        <Frag key={i} index={i} active={fragmentIndex}>
                            <div className="flex items-start gap-5">
                                <div className="mt-3 w-3 h-3 rounded-full sparkl-gradient shrink-0 shadow-[0_0_24px_rgba(91,194,204,0.45)]" />
                                <p className="text-3xl font-medium leading-snug text-[#14202a]">
                                    {f.bullet}
                                </p>
                            </div>
                        </Frag>
                    ))}
                </div>
            </div>
            <div className="col-span-5 flex justify-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9 }}
                    className="relative"
                >
                    <div className="absolute -inset-8 rounded-[40px] sparkl-gradient opacity-25 blur-3xl" />
                    <img
                        src={slide.heroImage}
                        alt={slide.title}
                        className="relative w-full max-w-[420px] aspect-square object-cover rounded-[36px] soft-shadow"
                    />
                </motion.div>
            </div>
        </div>
    </Stage>
);

const DiagramTeachingView = ({ slide, fragmentIndex }) => (
    <Stage>
        <div className="m-auto w-full max-w-[1400px] px-16 grid grid-cols-12 gap-12 items-center">
            <div className="col-span-7 flex justify-center">
                <motion.img
                    src={slide.diagramImage}
                    alt={slide.title}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1 }}
                    className="w-full max-w-[560px] aspect-square object-contain rounded-[40px] bg-white/40 p-6 soft-shadow"
                />
            </div>
            <div className="col-span-5">
                <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-5">
                    Diagram
                </p>
                <h2 className="text-5xl font-semibold tracking-tight leading-[1.05] mb-12">
                    {slide.title}
                </h2>
                <div className="space-y-7">
                    {slide.fragments.map((f, i) => (
                        <Frag key={i} index={i} active={fragmentIndex}>
                            <div className="flex items-start gap-4">
                                <span className="text-xs font-semibold tracking-[0.2em] text-[#2E8E9A] mt-3.5">
                                    0{i + 1}
                                </span>
                                <p className="text-2xl font-medium leading-snug text-[#14202a]">
                                    {f.label}
                                </p>
                            </div>
                        </Frag>
                    ))}
                </div>
            </div>
        </div>
    </Stage>
);

const VideoView = ({ slide, videoPlaying, videoRestartAt }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current) return;
        if (videoPlaying) ref.current.play().catch(() => {});
        else ref.current.pause();
    }, [videoPlaying]);

    useEffect(() => {
        if (!ref.current || !videoRestartAt) return;
        try {
            ref.current.currentTime = 0;
        } catch {
            /* noop */
        }
    }, [videoRestartAt]);

    return (
        <Stage>
            <div className="m-auto w-full max-w-[1400px] px-16">
                <div className="text-center mb-8">
                    <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-3">
                        Watch & observe
                    </p>
                    <h2 className="text-4xl font-semibold tracking-tight">
                        {slide.title}
                    </h2>
                </div>
                <div className="relative rounded-[36px] overflow-hidden soft-shadow bg-black aspect-video">
                    <video
                        ref={ref}
                        src={slide.videoSrc || slide.videoUrl}
                        poster={slide.videoPoster}
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover"
                        data-testid="student-video-player"
                    />
                    {!videoPlaying && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                            <div className="w-24 h-24 rounded-full sparkl-gradient flex items-center justify-center shadow-2xl">
                                <span className="text-white text-3xl pl-1">
                                    ▶
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Stage>
    );
};

const QuestionView = ({ slide, revealAnswer }) => (
    <Stage>
        <div className="m-auto w-full max-w-[1300px] px-16">
            <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-6 text-center">
                Quick check
            </p>
            <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="text-5xl md:text-6xl font-semibold tracking-tight text-center leading-[1.1] mb-16 max-w-4xl mx-auto"
            >
                {slide.question}
            </motion.h2>
            <div className="grid grid-cols-2 gap-6 max-w-4xl mx-auto">
                {slide.options.map((opt, i) => {
                    const isCorrect = i === slide.correctIndex;
                    return (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{
                                opacity: 1,
                                y: 0,
                                scale: revealAnswer && isCorrect ? 1.04 : 1,
                            }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            className={`px-8 py-7 rounded-3xl border-2 transition-all duration-500 ${
                                revealAnswer && isCorrect
                                    ? "bg-[#FFF1E5] border-[#FFA987]"
                                    : revealAnswer
                                      ? "bg-white/40 border-black/5 opacity-50"
                                      : "bg-white border-black/5"
                            }`}
                        >
                            <div className="flex items-center gap-5">
                                <span
                                    className={`w-11 h-11 rounded-xl flex items-center justify-center text-base font-semibold ${
                                        revealAnswer && isCorrect
                                            ? "bg-[#FF9A78] text-white"
                                            : "bg-[#E0F4F4] text-[#2E8E9A]"
                                    }`}
                                >
                                    {String.fromCharCode(65 + i)}
                                </span>
                                <span className="text-2xl font-medium">
                                    {opt}
                                </span>
                                {revealAnswer && isCorrect && (
                                    <span className="ml-auto text-2xl">✓</span>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    </Stage>
);

const ThinkPauseView = ({ slide }) => (
    <Stage>
        <div className="m-auto flex flex-col items-center text-center px-12 max-w-3xl">
            <Mascot state="listening" size="lg" />
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-12"
            >
                <p className="text-sm uppercase tracking-[0.3em] text-[#C75730] mb-5">
                    {slide.title}
                </p>
                <h2 className="text-6xl font-medium tracking-tight leading-[1.1]">
                    {slide.prompt}
                </h2>
            </motion.div>
        </div>
    </Stage>
);

const ActivityView = ({ slide, timerStartedAt, timerSeconds, timerPaused }) => (
    <Stage>
        <div className="m-auto w-full max-w-[1300px] px-16 grid grid-cols-12 gap-16 items-center">
            <div className="col-span-7">
                <p className="text-sm uppercase tracking-[0.3em] text-[#C75730] mb-5">
                    Class activity
                </p>
                <h2 className="text-5xl font-semibold tracking-tight leading-[1.1] mb-8">
                    {slide.title}
                </h2>
                <p className="text-3xl text-[#14202a] leading-relaxed font-normal">
                    {slide.instruction}
                </p>
            </div>
            <div className="col-span-5 flex justify-center">
                <TimerRing
                    total={timerSeconds || slide.timerSeconds}
                    startedAt={timerStartedAt}
                    paused={timerPaused}
                    size={340}
                />
            </div>
        </div>
    </Stage>
);

const SolutionView = ({ slide, solutionStep }) => (
    <Stage>
        <div className="m-auto w-full max-w-[1100px] px-16">
            <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-5">
                Solution · step by step
            </p>
            <h2 className="text-5xl font-semibold tracking-tight leading-[1.05] mb-12">
                {slide.title}
            </h2>
            <div className="space-y-5">
                {slide.steps.map((s, i) => {
                    if (i > solutionStep) return null;
                    const isCurrent = i === solutionStep;
                    const isAnswer = i === slide.steps.length - 1;
                    return (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                            animate={{
                                opacity: isCurrent ? 1 : 0.45,
                                y: 0,
                                filter: "blur(0px)",
                            }}
                            transition={{
                                duration: 0.6,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className={`p-8 rounded-3xl border ${
                                isCurrent && isAnswer
                                    ? "bg-[#FFF1E5] border-[#FFA987]"
                                    : isCurrent
                                      ? "bg-white border-black/5 soft-shadow"
                                      : "bg-white/50 border-black/5"
                            }`}
                        >
                            <div className="flex items-start gap-5">
                                <span
                                    className={`w-11 h-11 rounded-2xl flex items-center justify-center font-semibold ${
                                        isAnswer
                                            ? "bg-[#FF9A78] text-white"
                                            : "sparkl-gradient text-white"
                                    }`}
                                >
                                    {i + 1}
                                </span>
                                <div className="pt-1.5">
                                    <h4 className="text-2xl font-semibold tracking-tight mb-2">
                                        {s.title}
                                    </h4>
                                    <p className="text-xl text-[#4a5662] leading-relaxed">
                                        {s.body}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    </Stage>
);

const RecapView = ({ slide, fragmentIndex }) => (
    <Stage>
        <div className="m-auto w-full max-w-[1300px] px-16">
            <div className="text-center mb-14">
                <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-3">
                    Recap
                </p>
                <h2 className="text-6xl font-semibold tracking-tight">
                    {slide.title}
                </h2>
            </div>
            <div className="grid grid-cols-2 gap-6">
                {slide.takeaways.map((t, i) => (
                    <Frag
                        key={i}
                        index={i}
                        active={Math.max(0, fragmentIndex - 1)}
                    >
                        <div className="p-8 rounded-3xl bg-white border border-black/5 soft-shadow">
                            <div className="flex items-start gap-4">
                                <span className="w-9 h-9 rounded-xl sparkl-gradient flex items-center justify-center text-white text-sm font-semibold mt-0.5">
                                    {i + 1}
                                </span>
                                <div>
                                    <h4 className="text-2xl font-semibold tracking-tight mb-2">
                                        {t.head}
                                    </h4>
                                    <p className="text-lg text-[#4a5662] leading-relaxed">
                                        {t.body}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Frag>
                ))}
            </div>
        </div>
    </Stage>
);

const BlankView = () => (
    <div className="absolute inset-0 bg-[#F5FAFC] flex items-center justify-center">
        <motion.div
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="w-3 h-3 rounded-full sparkl-gradient"
        />
    </div>
);

const NotStartedView = () => (
    <Stage>
        <div className="m-auto flex flex-col items-center text-center px-12">
            <Mascot state="idle" size="lg" />
            <div className="mt-12">
                <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-3">
                    Waiting to begin
                </p>
                <h1 className="text-6xl font-semibold tracking-tight mb-4">
                    Ready when you are.
                </h1>
                <p className="text-2xl text-[#4a5662]">
                    Open the teacher panel to start the session.
                </p>
            </div>
        </div>
    </Stage>
);

/* ---- MCQ display (driven by activeMcqId) ---- */
const MCQView = ({ state }) => {
    const mcq = findMcqById(state.activeMcqId);
    if (!mcq)
        return (
            <Stage>
                <div className="m-auto text-center">
                    <p className="text-3xl text-[#4a5662]">
                        Select an MCQ from the teacher panel to display.
                    </p>
                </div>
            </Stage>
        );
    return (
        <Stage>
            <div className="m-auto w-full max-w-[1300px] px-16">
                <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-6 text-center">
                    Concept Check
                </p>
                <h2 className="text-5xl md:text-6xl font-semibold tracking-tight text-center leading-[1.1] mb-16 max-w-4xl mx-auto">
                    {mcq.question}
                </h2>
                <div className="grid grid-cols-2 gap-6 max-w-4xl mx-auto">
                    {mcq.options.map((opt, i) => {
                        const isCorrect = i === mcq.correctIndex;
                        return (
                            <div
                                key={i}
                                className={`px-8 py-7 rounded-3xl border-2 transition-all duration-500 ${
                                    state.revealMcqAnswer && isCorrect
                                        ? "bg-[#FFF1E5] border-[#FFA987] scale-[1.02]"
                                        : state.revealMcqAnswer
                                          ? "bg-white/40 border-black/5 opacity-50"
                                          : "bg-white border-black/5"
                                }`}
                            >
                                <div className="flex items-center gap-5">
                                    <span
                                        className={`w-11 h-11 rounded-xl flex items-center justify-center text-base font-semibold ${
                                            state.revealMcqAnswer && isCorrect
                                                ? "bg-[#FF9A78] text-white"
                                                : "bg-[#E0F4F4] text-[#2E8E9A]"
                                        }`}
                                    >
                                        {String.fromCharCode(65 + i)}
                                    </span>
                                    <span className="text-2xl font-medium">
                                        {opt}
                                    </span>
                                    {state.revealMcqAnswer && isCorrect && (
                                        <span className="ml-auto text-2xl">✓</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {state.revealMcqAnswer && mcq.explanation && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-10 p-6 rounded-2xl bg-[#E0F4F4] border border-[#5BC2CC]/30 max-w-3xl mx-auto"
                    >
                        <p className="text-xl text-[#14202a] leading-relaxed text-center">
                            {mcq.explanation}
                        </p>
                    </motion.div>
                )}
            </div>
        </Stage>
    );
};

/* ---- Subjective display (solved-example or practice) ---- */
const SubjectiveView = ({ state }) => {
    const q = findSubjectiveById(state.activeSubjectiveId);
    if (!q)
        return (
            <Stage>
                <div className="m-auto text-center">
                    <p className="text-3xl text-[#4a5662]">
                        Select a question from the teacher panel.
                    </p>
                </div>
            </Stage>
        );
    const isSolved = state.subjectiveMode === "solved";
    return (
        <Stage>
            <div className="m-auto w-full max-w-[1200px] px-16">
                <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-5 text-center">
                    {isSolved ? "Solved Example" : "Practice"}
                </p>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-center leading-[1.15] mb-12 max-w-4xl mx-auto">
                    {q.question}
                </h2>

                {(isSolved && ((q.steps && q.steps.length > 0) || (q.expectedPoints && q.expectedPoints.length > 0))) && (
                    <div className="space-y-4 max-w-3xl mx-auto">
                        {q.steps && q.steps.map((s, i) => {
                            return (
                                <motion.div
                                    key={`step-${i}`}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                    }}
                                    className="p-6 rounded-3xl border bg-white soft-shadow border-black/5"
                                >
                                    <div className="flex items-start gap-4">
                                        <span className="w-9 h-9 rounded-xl sparkl-gradient flex items-center justify-center text-white text-sm font-semibold shrink-0">
                                            {i + 1}
                                        </span>
                                        <div>
                                            <h4 className="text-xl font-semibold tracking-tight mb-1">
                                                {s.title}
                                            </h4>
                                            <p className="text-lg text-[#4a5662] leading-relaxed">
                                                {s.body}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                        {q.expectedPoints && q.expectedPoints.map((point, i) => {
                            const offset = q.steps ? q.steps.length : 0;
                            return (
                                <motion.div
                                    key={`point-${i}`}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                    }}
                                    className="p-6 rounded-3xl border bg-white soft-shadow border-black/5"
                                >
                                    <div className="flex items-start gap-4">
                                        <span className="w-9 h-9 rounded-xl sparkl-gradient flex items-center justify-center text-white text-sm font-semibold shrink-0">
                                            {offset + i + 1}
                                        </span>
                                        <div>
                                            <p className="text-lg text-[#1e293b] leading-relaxed font-medium">
                                                {point}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {!isSolved && (
                    <div className="text-center max-w-3xl mx-auto">
                        {state.timerStartedAt && (
                            <div className="mb-8">
                                <TimerRing
                                    total={state.timerSeconds || q.estimatedSeconds}
                                    startedAt={state.timerStartedAt}
                                    paused={state.timerPaused}
                                    size={240}
                                />
                            </div>
                        )}
                        {!state.timerStartedAt && (
                            <p className="text-2xl text-[#4a5662] mb-4">
                                {q.hint || "Try this in your notebook."}
                            </p>
                        )}
                        {state.revealExpected && q.expectedPoints && (
                            <motion.div
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-6 p-6 rounded-3xl bg-[#E0F4F4] border border-[#5BC2CC]/30 text-left"
                            >
                                <p className="text-sm uppercase tracking-[0.2em] text-[#2E8E9A] font-semibold mb-3">
                                    Expected points
                                </p>
                                <ul className="space-y-2">
                                    {q.expectedPoints.map((p, i) => (
                                        <li
                                            key={i}
                                            className="text-lg text-[#14202a] flex gap-2"
                                        >
                                            <span className="text-[#2E8E9A]">●</span>
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        )}
                        {state.revealCommonMistake && q.commonMistake && (
                            <motion.div
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-4 p-5 rounded-2xl bg-[#FFF1E5] border border-[#FFA987]/30 text-left"
                            >
                                <p className="text-sm uppercase tracking-[0.2em] text-[#C75730] font-semibold mb-2">
                                    Common mistake
                                </p>
                                <p className="text-lg text-[#14202a]">
                                    {q.commonMistake}
                                </p>
                            </motion.div>
                        )}
                    </div>
                )}
            </div>
        </Stage>
    );
};

/* ---- Drag & drop activity (real mouse-drag using pointer events) ---- */
const DragDropView = ({ state, dragDropProps = {} }) => {
    const a = lesson.activity;
    const { interactive = false, onPlace, onPickup } = dragDropProps;
    const placements = state.dragPlacements || {};
    const check = state.dragCheckResult;

    const [drag, setDrag] = useState(null);
    // { id, x, y, label, ox, oy } where x,y is current pointer (viewport),
    // ox/oy is the original element top/left so we can offset the ghost nicely.
    const dragRef = useRef(null);
    dragRef.current = drag;

    // Window-level listeners while dragging — work regardless of CSS transforms.
    useEffect(() => {
        if (!drag) return;

        const onMove = (e) => {
            setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
        };
        const findZone = (x, y) => {
            // Temporarily hide ghost so elementFromPoint sees what's beneath.
            const els = document.elementsFromPoint(x, y);
            for (const el of els) {
                let node = el;
                while (node) {
                    const tid = node.getAttribute?.("data-testid");
                    if (tid?.startsWith("dragdrop-cat-"))
                        return tid.slice("dragdrop-cat-".length);
                    if (tid === "dragdrop-bank") return "bank";
                    node = node.parentElement;
                }
            }
            return null;
        };
        const onUp = (e) => {
            const cur = dragRef.current;
            if (!cur) return;
            const zone = findZone(e.clientX, e.clientY);
            if (zone === "bank") onPickup?.(cur.id);
            else if (zone) onPlace?.(cur.id, zone);
            setDrag(null);
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
        };
    }, [drag?.id, onPlace, onPickup]);

    const onItemPointerDown = (e, item) => {
        if (!interactive) return;
        e.preventDefault();
        e.stopPropagation();
        setDrag({
            id: item.id,
            label: item.label,
            x: e.clientX,
            y: e.clientY,
        });
    };

    const itemBank = a.items.filter((it) => !placements[it.id]);
    const itemsIn = (catId) =>
        a.items.filter((it) => placements[it.id] === catId);

    const itemClass = (it) => {
        const r = check?.checks?.[it.id];
        if (r === "correct") return "bg-[#DCEFEC] border-[#3B9C82] text-[#1F5E50]";
        if (r === "wrong") return "bg-red-50 border-red-300 text-red-700";
        return "bg-white border-[#5BC2CC]/40";
    };

    const renderItem = (it, inBank = false) => {
        const isDragging = drag?.id === it.id;
        return (
            <span
                key={it.id}
                onPointerDown={(e) => onItemPointerDown(e, it)}
                data-testid={`dragdrop-item-${it.id}`}
                style={{
                    touchAction: "none",
                    visibility: isDragging ? "hidden" : "visible",
                }}
                className={`px-4 py-2 ${
                    inBank ? "rounded-xl text-base" : "rounded-full text-sm"
                } border-2 font-${inBank ? "medium" : "semibold"} select-none transition-all ${
                    interactive ? "cursor-grab active:cursor-grabbing" : ""
                } ${itemClass(it)}`}
            >
                {it.label}
            </span>
        );
    };

    return (
        <Stage>
            <div className="m-auto w-full max-w-[1500px] px-16">
                <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-3 text-center">
                    Activity · Drag & Drop
                </p>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-center mb-3">
                    {a.title}
                </h2>
                <p className="text-xl text-[#4a5662] text-center mb-8">
                    {a.instruction}
                </p>

                <div className="grid grid-cols-2 gap-8 mb-8 max-w-5xl mx-auto">
                    {a.categories.map((cat) => (
                        <div
                            key={cat.id}
                            data-testid={`dragdrop-cat-${cat.id}`}
                            className={`p-8 rounded-3xl border-2 border-dashed transition-all ${
                                cat.id === "inputs"
                                    ? "border-[#5BC2CC] bg-[#E0F4F4]/40"
                                    : "border-[#FFA987] bg-[#FFF1E5]/40"
                            } min-h-[180px] ${
                                drag
                                    ? "ring-4 ring-offset-2 ring-[#5BC2CC]/30"
                                    : ""
                            }`}
                        >
                            <p
                                className={`text-xs uppercase tracking-[0.3em] font-semibold mb-4 ${
                                    cat.id === "inputs"
                                        ? "text-[#2E8E9A]"
                                        : "text-[#C75730]"
                                }`}
                            >
                                {cat.label}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {itemsIn(cat.id).map((it) => renderItem(it))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Item bank — also a drop zone for moving items back */}
                <div
                    data-testid="dragdrop-bank"
                    className={`max-w-5xl mx-auto p-6 rounded-3xl bg-white border border-black/5 soft-shadow transition-all ${
                        drag && placements[drag.id]
                            ? "ring-4 ring-offset-2 ring-[#FFA987]/40"
                            : ""
                    }`}
                >
                    <p className="text-xs uppercase tracking-[0.3em] text-[#4a5662] font-semibold mb-3">
                        Item bank
                    </p>
                    <div className="flex flex-wrap gap-3">
                        {itemBank.length === 0 && (
                            <p className="text-sm text-[#4a5662] italic">
                                All items placed.
                            </p>
                        )}
                        {itemBank.map((it) => renderItem(it, true))}
                    </div>
                </div>

                {check && (
                    <div
                        className="mt-6 text-center"
                        data-testid="dragdrop-result"
                    >
                        <p className="text-3xl font-semibold tracking-tight">
                            {check.correct} / {check.total}{" "}
                            <span className="text-[#4a5662] text-2xl font-normal">
                                correct
                            </span>
                        </p>
                    </div>
                )}
            </div>
            {/* Floating ghost — rendered via portal to escape CSS transforms */}
            {drag && interactive &&
                createPortal(
                    <div
                        style={{
                            position: "fixed",
                            left: drag.x,
                            top: drag.y,
                            transform: "translate(-50%, -50%) rotate(-2deg)",
                            pointerEvents: "none",
                            zIndex: 9999,
                        }}
                        className="px-4 py-2 rounded-xl border-2 border-[#5BC2CC] bg-white font-semibold text-base text-[#14202a] shadow-[0_12px_28px_rgba(0,0,0,0.18)]"
                    >
                        {drag.label}
                    </div>,
                    document.body,
                )}
        </Stage>
    );
};

/* ---- Real-life example slide (image + analogy) ---- */
const RealLifeExampleView = ({ state, storyboard }) => {
    const ex = findExampleById(state.activeExampleId);
    if (!ex)
        return (
            <Stage>
                <div className="m-auto text-center">
                    <p className="text-3xl text-[#4a5662]">
                        Select a real-life example from the teacher panel.
                    </p>
                </div>
            </Stage>
        );

    const entry = findExampleStoryboardEntry(storyboard, state.activeExampleId);
    const displayText = entry?.narration || entry?.text || ex.description || ex.text;
    const headline = entry?.text && entry?.text !== displayText ? entry.text : (ex.headline || ex.text);

    return (
        <Stage>
            <div className="m-auto w-full max-w-[1500px] px-16 grid grid-cols-12 gap-12 items-center">
                <div className="col-span-7 flex justify-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative"
                    >
                        <div className="absolute -inset-8 rounded-[44px] sparkl-gradient opacity-25 blur-3xl" />
                        <img
                            src={ex.image}
                            alt={headline}
                            className="relative w-full max-w-[640px] aspect-[4/3] object-cover rounded-[36px] soft-shadow"
                            data-testid="real-life-image"
                        />
                    </motion.div>
                </div>
                <div className="col-span-5">
                    <p className="text-sm uppercase tracking-[0.3em] text-[#C75730] mb-5">
                        Real-life example
                    </p>
                    <h2 className="text-5xl font-semibold tracking-tight leading-[1.05] mb-8">
                        {headline}
                    </h2>
                    <p className="text-2xl text-[#14202a] leading-relaxed font-normal whitespace-pre-wrap">
                        {displayText}
                    </p>
                </div>
            </div>
        </Stage>
    );
};

/* ---- Blank teaching slide (whiteboard) ---- */
const BlankSlideView = ({ state, photoOverride }) => {
    const t = photoOverride
        ? "photo"
        : state.blankSlideType || "white";
    // Special case: photo blank slate — render the captured student
    // photo as the slide content. Teacher can annotate over it via the
    // smartboard's annotation toolbar.
    const photo = photoOverride || state.studentAnswerPhoto;
    if (t === "photo" && photo) {
        return (
            <Stage>
                <div className="absolute inset-0 bg-white">
                    <img
                        src={photo}
                        alt="Student work"
                        style={{
                            position: "absolute",
                            top: "30px",
                            bottom: "30px",
                            left: "30px",
                            height: "calc(100% - 60px)",
                            objectFit: "contain",
                            borderRadius: "12px",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                        }}
                        data-testid="blank-slide-photo"
                    />
                </div>
            </Stage>
        );
    }
    const bg =
        t === "lined"
            ? "repeating-linear-gradient(0deg, transparent 0 32px, rgba(20,32,42,0.06) 32px 33px)"
            : t === "grid"
              ? "repeating-linear-gradient(0deg, transparent 0 32px, rgba(20,32,42,0.06) 32px 33px), repeating-linear-gradient(90deg, transparent 0 32px, rgba(20,32,42,0.06) 32px 33px)"
              : "transparent";
    return (
        <Stage>
            <div className="absolute inset-0 bg-white" />
            <div
                className="absolute inset-0"
                style={{ backgroundImage: bg }}
            />
            {state.aiHandwritingResult && state.aiHelpShowToStudents && (
                <div className="m-auto max-w-[900px] z-10 px-16">
                    <p className="text-sm uppercase tracking-[0.3em] text-[#2E8E9A] mb-4 text-center">
                        AI cleaned version
                    </p>
                    <p className="text-5xl font-semibold tracking-tight text-center text-[#14202a] leading-tight">
                        {state.aiHandwritingResult.cleanedVersion}
                    </p>
                </div>
            )}
        </Stage>
    );
};


/* -------- main reusable Stage -------- */

/**
 * findMascotSlot — given a list of foreground content rectangles (in
 * 1920x1080 logical px), pick the largest mascot size & best slot.
 *
 * Strategy:
 *   1. Try lg (360), then md (220), then sm (110).
 *   2. For each size, evaluate 6 candidate slots (top/mid/bottom × left/right).
 *      The first slot that has zero overlap with any content rect wins.
 *   3. If no slot at any size is clear, return the sm-sized corner that
 *      minimises the overlap area with content (user pref: keep mascot
 *      tiny in the least crowded corner on dense slides).
 */
const SLOT_NAMES = [
    "mid-right",
    "mid-left",
    "bottom-right",
    "bottom-left",
    "top-right",
    "top-left",
];

const computeSlotPos = (slotName, dim) => {
    const pad = 48;
    const xRight = 1920 - pad - dim;
    const xLeft = pad;
    const yMid = (1080 - dim) / 2;
    const yTop = 64;
    const yBottom = 1080 - dim - 80;
    switch (slotName) {
        case "top-right":
            return { left: xRight, top: yTop };
        case "top-left":
            return { left: xLeft, top: yTop };
        case "mid-right":
            return { left: xRight, top: yMid };
        case "mid-left":
            return { left: xLeft, top: yMid };
        case "bottom-right":
            return { left: xRight, top: yBottom };
        case "bottom-left":
            return { left: xLeft, top: yBottom };
        default:
            return { left: xRight, top: yMid };
    }
};

const overlapArea = (a, b) => {
    const w = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
    const h = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
    return w * h;
};

export const findMascotSlot = (avoidRects) => {
    for (const dim of [360, 220, 110]) {
        for (const slot of SLOT_NAMES) {
            const { left, top } = computeSlotPos(slot, dim);
            const mascotRect = {
                left,
                top,
                right: left + dim,
                bottom: top + dim,
            };
            const overlaps = avoidRects.some(
                (r) => overlapArea(mascotRect, r) > 0,
            );
            if (!overlaps) {
                return { left, top, dim, slot };
            }
        }
    }
    // No clear slot at any size. Find sm corner with least overlap.
    const dim = 110;
    let best = { left: 0, top: 0, dim, slot: "top-right", overlap: Infinity };
    for (const slot of SLOT_NAMES) {
        const { left, top } = computeSlotPos(slot, dim);
        const mascotRect = {
            left,
            top,
            right: left + dim,
            bottom: top + dim,
        };
        const total = avoidRects.reduce(
            (acc, r) => acc + overlapArea(mascotRect, r),
            0,
        );
        if (total < best.overlap) {
            best = { left, top, dim, slot, overlap: total };
        }
    }
    return { left: best.left, top: best.top, dim: best.dim, slot: best.slot };
};

export const LessonStage = ({
    state,
    showChrome = true,
    annotationProps = {},
    dragDropProps = {},
    onExamplePopupClose,
    playAudio = false,
    onAudioStarted,
    onAudioEnded,
    onAudioError,
    onAudioMetadata,
}) => {
    const slide = lesson.slides[state.slideIndex] || lesson.slides[0];
    // When the teacher captures a photo on slide N, we inject a
    // session-only blank "photo slide" AFTER slide N (e.g. 5.1, 5.2).
    // `subSlideIndex > 0` means we're rendering the nth extra after
    // `slideIndex`. The photo data URL lives in
    // state.photoExtras[slideIndex][subSlideIndex - 1].
    const photoSubSlide = (() => {
        const sub = state.subSlideIndex || 0;
        if (sub <= 0) return null;
        const extras = state.photoExtras?.[state.slideIndex] || [];
        const photo = extras[sub - 1];
        return photo ? { photo, sub } : null;
    })();
    // When teacher pushes a captured student photo, we treat it as a
    // BLANK SLIDE (white slate with the photo on it) — not the
    // "ambient" blank_screen mode that just shows a pulsing dot.
    const photoBlank =
        !!photoSubSlide ||
        (state.blanked &&
            state.blankSlideType === "photo" &&
            !!state.studentAnswerPhoto);
    const mode =
        state.blanked && !photoBlank ? "blank_screen" : slide.mode;

    /* ---- Storyboard audio playback (student display only) ---- */
    const audioRef = useRef(null);
    const [storyboard, setStoryboard] = useState(null);
    const blobCacheRef = useRef({}); // { [originalUrl]: localBlobUrl }
    const activeAudioUrlRef = useRef(null);
    const lastPlayedRef = useRef({ slideIndex: null, fragmentIndex: null, examplePopup: null });

    // Clean up all object URLs on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            const cache = blobCacheRef.current;
            Object.values(cache).forEach((url) => {
                try {
                    URL.revokeObjectURL(url);
                } catch (e) {
                    /* noop */
                }
            });
        };
    }, []);

    // Background pre-fetching of all storyboard audios into the Cache API
    useEffect(() => {
        if (!playAudio || !storyboard) return;
        
        let active = true;
        const prefetchAudios = async () => {
            try {
                const { cacheAllAudios } = await import("../lib/audioCache");
                if (!active) return;
                await cacheAllAudios(storyboard);
            } catch (e) {
                console.warn("[LessonStage] Background prefetch into Cache API failed:", e);
            }
        };
        
        prefetchAudios();
        
        return () => {
            active = false;
        };
    }, [playAudio, storyboard]);

    const getOriginalUrl = useCallback((src) => {
        if (!src) return src;
        for (const [orig, blobUrl] of Object.entries(blobCacheRef.current)) {
            if (blobUrl === src) return orig;
        }
        return src;
    }, []);
    // Resolve lesson id from URL so we load the right storyboard
    // (e.g. quadratic-equations vs photosynthesis) — otherwise the
    // photosynthesis storyboard would play over every lesson.
    const currentLessonId = useMemo(() => {
        try {
            return (
                new URLSearchParams(window.location.search).get("lessonId") ||
                "photosynthesis"
            );
        } catch {
            return "photosynthesis";
        }
    }, []);

    useEffect(() => {
        setStoryboard(loadStoredStoryboard(currentLessonId));
        // Watch for cross-tab updates from the Audio Studio.
        const onStorage = (e) => {
            if (
                e.key === `sparkl-storyboard-${currentLessonId}-v1` ||
                e.key === "sparkl-storyboard-photosynthesis-v1"
            ) {
                setStoryboard(loadStoredStoryboard(currentLessonId));
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [currentLessonId]);

    /* Pause audio when the tab is hidden; RESUME when it becomes visible
       again. Without the resume path the smartboard would stay silent
       after the user briefly focused another tab / minimised the window —
       a known cause of "audio just stopped playing" reports. We resume by
       bumping a counter that re-runs the playback effect with the same
       slide/fragment, so it follows the regular load/play path
       (preserving popup-priority, audio-ownership, hasAudioOwnership). */
    const [audioResumeNonce, setAudioResumeNonce] = useState(0);
    useEffect(() => {
        if (!playAudio) return;
        const el = audioRef.current;
        if (!el) return;
        const onVisibility = () => {
            if (document.hidden) {
                try {
                    el.pause();
                } catch {
                    /* noop */
                }
            } else {
                // Tab is visible again. Bump a nonce so the playback
                // effect below re-runs and re-evaluates whether audio
                // should be playing right now.
                setAudioResumeNonce((n) => n + 1);
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () =>
            document.removeEventListener("visibilitychange", onVisibility);
    }, [playAudio]);

    useEffect(() => {
        if (!playAudio) return;
        const el = audioRef.current;
        if (!el) return;

        // If this tab isn't currently visible, never start playback here.
        if (document.hidden) {
            try {
                el.pause();
            } catch {
                /* noop */
            }
            return;
        }

        // Real-life example popup takes priority and ignores most pause states —
        // the teacher intentionally chose to show this bonus to the class.
        let targetUrl = null;
        let isPopup = false;

        const popupEntry = state.examplePopup
            ? findExampleStoryboardEntry(storyboard, state.examplePopup)
            : null;

        if (popupEntry?.audio) {
            targetUrl = popupEntry.audio;
            isPopup = true;
        } else if (
            !state.aiPaused &&
            !state.blanked &&
            (state.subSlideIndex || 0) === 0 &&
            state.sessionStarted &&
            !state.sessionEnded &&
            state.slideSelected !== false &&
            state.slideRenderMode !== "full"
        ) {
            let entry = null;
            if (slide?.mode === "mcq_display" && state.activeMcqId) {
                if (state.revealMcqAnswer) {
                    entry = findMcqExplanationStoryboardEntry(storyboard, state.activeMcqId);
                } else {
                    entry = findMcqStoryboardEntry(storyboard, state.activeMcqId);
                }
            } else if (slide?.mode === "real_life_example" && state.activeExampleId) {
                entry = findExampleStoryboardEntry(storyboard, state.activeExampleId);
            }
            if (!entry) {
                entry = findStoryboardEntry(
                    storyboard,
                    state.slideIndex,
                    state.fragmentIndex,
                );
            }
            if (entry?.audio) {
                targetUrl = entry.audio;
            }
        }

        // If no target URL, pause immediately and cancel speech synthesis
        if (!targetUrl) {
            try {
                el.pause();
                if (window.speechSynthesis) {
                    window.speechSynthesis.cancel();
                }
            } catch {
                /* noop */
            }
            activeAudioUrlRef.current = null;
            return;
        }

        activeAudioUrlRef.current = targetUrl;

        // Clean up speech synthesis if we're not a popup
        if (!isPopup) {
            try {
                if (window.speechSynthesis) {
                    window.speechSynthesis.cancel();
                }
            } catch {
                /* noop */
            }
        }

        const isSameFragment =
            lastPlayedRef.current.slideIndex === state.slideIndex &&
            lastPlayedRef.current.fragmentIndex === state.fragmentIndex &&
            lastPlayedRef.current.examplePopup === state.examplePopup;

        const clean = (s) => s ? s.replace(/^(https?:)?\/\/[^\/]+/, "").replace(/^\//, "") : "";
        const currentBlobUrl = blobCacheRef.current[targetUrl];
        const isSameSource = isSameFragment && el.src && el.src !== "about:blank" && el.src !== "" && (
            clean(el.src) === clean(targetUrl) || (currentBlobUrl && clean(el.src) === clean(currentBlobUrl))
        );

        const playSource = (src) => {
            try {
                el.src = src;
                el.loop = false;
                el.currentTime = 0;
                lastPlayedRef.current = {
                    slideIndex: state.slideIndex,
                    fragmentIndex: state.fragmentIndex,
                    examplePopup: state.examplePopup,
                };
                const p = el.play();
                if (p?.catch) {
                    p.catch((err) => {
                        console.warn("[audio] play() rejected:", err?.name, err?.message);
                        setTimeout(() => {
                            if (activeAudioUrlRef.current === targetUrl) {
                                try {
                                    const p2 = el.play();
                                    if (p2?.catch) p2.catch(() => {});
                                } catch {
                                    /* noop */
                                }
                            }
                        }, 250);
                    });
                }
            } catch {
                /* noop */
            }
        };

        const loadAndPlay = async (url) => {
            if (activeAudioUrlRef.current === url) {
                playSource(url);
            }
        };

        if (isSameSource) {
            if (el.paused) {
                try {
                    const p = el.play();
                    if (p?.catch) {
                        p.catch((err) => {
                            console.warn("[audio] resume play() rejected:", err?.name, err?.message);
                            setTimeout(() => {
                                if (activeAudioUrlRef.current === targetUrl) {
                                    try {
                                        const p2 = el.play();
                                        if (p2?.catch) p2.catch(() => {});
                                    } catch {
                                        /* noop */
                                    }
                                }
                            }, 250);
                        });
                    }
                } catch {
                    /* noop */
                }
            }
        } else {
            // Source changed, so we pause first
            try {
                el.pause();
            } catch {
                /* noop */
            }

            if (currentBlobUrl) {
                playSource(currentBlobUrl);
            } else {
                loadAndPlay(targetUrl);
            }
        }
    }, [
        playAudio,
        storyboard,
        state.slideIndex,
        state.fragmentIndex,
        state.examplePopup,
        state.aiPaused,
        state.blanked,
        state.subSlideIndex,
        state.studentAnswerPhoto,
        state.sessionStarted,
        state.sessionEnded,
        state.slideSelected,
        state.slideRenderMode,
        audioResumeNonce,
        state.activeMcqId,
        state.revealMcqAnswer,
        state.activeExampleId,
    ]);

    const mascotState = state.mascotState && (state.mascotState === "speaking" || state.mascotState === "listening" || state.mascotState === "thinking")
        ? state.mascotState
        : state.aiPaused
          ? "idle"
          : state.mascotState && state.mascotState !== "auto"
            ? state.mascotState
            : defaultMascotForMode(slide.mode);

    // 'full' render → show every fragment at once by passing a huge fragment index.
    const isFull = state.slideRenderMode === "full";
    const totalFrag = slide.fragments?.length || 0;
    const effectiveFragmentIndex = isFull
        ? Math.max(totalFrag - 1, state.fragmentIndex || 0)
        : state.fragmentIndex || 0;

    const currentFragment = slide.fragments?.[state.fragmentIndex];

    // Resolve matching storyboard entry for dynamic/uploaded narration transcript text
    let matchingStoryboardEntry = null;
    if (storyboard) {
        if (slide?.mode === "mcq_display" && state.activeMcqId) {
            if (state.revealMcqAnswer) {
                matchingStoryboardEntry = storyboard.find(
                    (e) => e.type === "mcq_explanation" && (e.mcq_id === state.activeMcqId || e.slug === `${state.activeMcqId}_explanation`)
                );
            } else {
                matchingStoryboardEntry = storyboard.find(
                    (e) => e.type === "mcq" && (e.mcq_id === state.activeMcqId || e.slug === state.activeMcqId)
                );
            }
        } else if (slide?.mode === "real_life_example" && state.activeExampleId) {
            matchingStoryboardEntry = storyboard.find(
                (e) => e.type === "example" && (e.example_id === state.activeExampleId || e.slug === state.activeExampleId)
            );
        } else {
            const slideNo = state.slideIndex + 1;
            matchingStoryboardEntry = storyboard.find(
                (e) =>
                    e.slide_no === slideNo &&
                    (e.fragment_no ?? 0) === (state.fragmentIndex ?? 0) &&
                    (e.type === "say" || e.type === "concept_check")
            );
        }
    }

    const captionText =
        state.captionOverride ||
        matchingStoryboardEntry?.narration ||
        currentFragment?.caption ||
        "";

    const showCaption =
        !state.blanked &&
        !state.aiPaused &&
        state.sessionStarted &&
        state.slideSelected !== false &&
        !isFull &&
        !!captionText;

    const corner = mode === "video_teaching";
    // Photo subslides get their own annotation key so the teacher's
    // marks don't pollute the main slide's annotations (and vice versa).
    const annoKey = photoSubSlide
        ? `${slide.id}::photo-${photoSubSlide.sub}`
        : slide.id;
    const slideStrokes = state.annotations?.[annoKey] || [];
    const popupExample = state.examplePopup
        ? lesson.realLifeExamples.find((e) => e.id === state.examplePopup)
        : null;
    const showWelcome = state.sessionStarted && state.slideSelected === false;

    /* ─────────────── Smart Mascot positioning ───────────────
       After each fragment renders, scan the stage for foreground
       content (`[data-mascot-avoid]` elements) and place the mascot
       in the LARGEST empty slot from a 6-position grid. Falls back to
       a smaller mascot if a bigger size can't fit, and finally to the
       least-overlapping corner if even the small size is contested.
       The background image / video / pageBackground are NOT counted —
       only foreground cards/charts/headers carry the marker. */
    const stageRef = useRef(null);
    const [mascotPlacement, setMascotPlacement] = useState({
        left: 1652,
        top: 430,
        dim: 220,
    });

    useEffect(() => {
        const stageEl = stageRef.current;
        if (!stageEl) return;
        if (!state.sessionStarted || state.sessionEnded) return;
        if (showWelcome || state.blanked || state.aiPaused) return;

        const measure = () => {
            const stageRect = stageEl.getBoundingClientRect();
            if (stageRect.width === 0) return;
            const scale = stageRect.width / 1920 || 1;
            const avoidRects = Array.from(
                stageEl.querySelectorAll("[data-mascot-avoid]"),
            ).map((el) => {
                const r = el.getBoundingClientRect();
                return {
                    left: (r.left - stageRect.left) / scale,
                    top: (r.top - stageRect.top) / scale,
                    right: (r.right - stageRect.left) / scale,
                    bottom: (r.bottom - stageRect.top) / scale,
                };
            });
            setMascotPlacement(findMascotSlot(avoidRects));
        };

        // Wait for fragment reveal animations (~600ms) to settle before
        // measuring — otherwise we'd land on a transient layout.
        const t1 = setTimeout(measure, 650);
        return () => clearTimeout(t1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        state.slideIndex,
        state.fragmentIndex,
        state.sessionStarted,
        state.sessionEnded,
        state.blanked,
        state.aiPaused,
        showWelcome,
    ]);

    return (
        <ScaledStage
            pageBackground={slide?.pageBackground}
            innerRef={stageRef}
        >
            {/* All children render in 1920x1080 logical coords */}
            <div className="absolute inset-0">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={mode + ":" + state.slideIndex}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute inset-0"
                        data-testid="student-stage"
                        data-mode={mode}
                    >
                        {!state.sessionStarted && !state.sessionEnded && (
                            <NotStartedView />
                        )}

                        {state.sessionEnded && <SessionEndedView />}

                        {state.sessionStarted && !state.sessionEnded && showWelcome && (
                            <SessionWelcomeView />
                        )}

                        {state.sessionStarted &&
                            !state.sessionEnded &&
                            !showWelcome &&
                            mode === "blank_screen" && <BlankView />}

                        {state.sessionStarted &&
                            !state.sessionEnded &&
                            !showWelcome &&
                            photoBlank && (
                                <BlankSlideView
                                    state={state}
                                    photoOverride={photoSubSlide?.photo}
                                />
                            )}

                        {state.sessionStarted &&
                            !state.sessionEnded &&
                            !showWelcome &&
                            mode !== "blank_screen" &&
                            !photoBlank && (
                                <>
                                    {isAngleRichSlide(slide.id) ? (
                                        <AngleSlide
                                            slide={slide}
                                            fragmentIndex={effectiveFragmentIndex}
                                            activeQuestion={state.activeQuestion}
                                        />
                                    ) : isSoundWaveRichSlide(slide.id) ? (
                                        <SoundWaveSlide
                                            slide={slide}
                                            fragmentIndex={effectiveFragmentIndex}
                                            activeQuestion={state.activeQuestion}
                                        />
                                    ) : (
                                    <>
                                    {slide.mode === "intro" && (
                                        <IntroView
                                            slide={slide}
                                            fragmentIndex={effectiveFragmentIndex}
                                        />
                                    )}
                                    {slide.mode === "concept_teaching" && (
                                        isQuadraticRichSlide(slide.id) ? (
                                            <QuadraticSlide
                                                slide={slide}
                                                fragmentIndex={effectiveFragmentIndex}
                                            />
                                        ) : (
                                            <ConceptTeachingView
                                                slide={slide}
                                                fragmentIndex={effectiveFragmentIndex}
                                            />
                                        )
                                    )}
                                    {slide.mode === "diagram_teaching" && (
                                        isQuadraticRichSlide(slide.id) ? (
                                            <QuadraticSlide
                                                slide={slide}
                                                fragmentIndex={effectiveFragmentIndex}
                                            />
                                        ) : (
                                            <DiagramTeachingView
                                                slide={slide}
                                                fragmentIndex={effectiveFragmentIndex}
                                            />
                                        )
                                    )}
                                    {slide.mode === "video_teaching" && (
                                        <VideoView
                                            slide={slide}
                                            videoPlaying={state.videoPlaying}
                                            videoRestartAt={state.videoRestartAt}
                                        />
                                    )}
                                    {slide.mode === "question_display" && (
                                        <QuestionView
                                            slide={slide}
                                            revealAnswer={state.revealAnswer}
                                        />
                                    )}
                                    {slide.mode === "think_pause" && (
                                        <ThinkPauseView slide={slide} />
                                    )}
                                    {slide.mode === "practice_activity" && (
                                        <ActivityView
                                            slide={slide}
                                            timerStartedAt={
                                                state.timerStartedAt
                                            }
                                            timerSeconds={
                                                state.timerSeconds ||
                                                slide.timerSeconds
                                            }
                                            timerPaused={state.timerPaused}
                                        />
                                    )}
                                    {slide.mode === "solution_reveal" && (
                                        <SolutionView
                                            slide={slide}
                                            solutionStep={state.solutionStep}
                                        />
                                    )}
                                    {slide.mode === "recap" && (
                                        <RecapView
                                            slide={slide}
                                            fragmentIndex={effectiveFragmentIndex}
                                        />
                                    )}
                                    {slide.mode === "mcq_display" && (
                                        <MCQView state={state} />
                                    )}
                                    {slide.mode === "subjective_display" && (
                                        <SubjectiveView state={state} />
                                    )}
                                    {slide.mode === "drag_drop" && (
                                        <DragDropView
                                            state={state}
                                            dragDropProps={dragDropProps}
                                        />
                                    )}
                                    {slide.mode === "blank_slide" && (
                                        <BlankSlideView state={state} />
                                    )}
                                    {slide.mode === "real_life_example" && (
                                        <RealLifeExampleView state={state} storyboard={storyboard} />
                                    )}
                                </>
                                    )}
                                </>
                            )}
                    </motion.div>
                </AnimatePresence>

                {/* Hidden audio element used for storyboard narration playback */}
                {playAudio && (
                    <audio
                        ref={audioRef}
                        data-testid="storyboard-audio"
                        style={{ display: "none" }}
                        preload="auto"
                        onPlay={(e) => {
                            if (onAudioStarted) {
                                onAudioStarted(getOriginalUrl(e.target.src));
                            }
                        }}
                        onEnded={(e) => {
                            if (onAudioEnded) {
                                onAudioEnded(getOriginalUrl(e.target.src));
                            }
                        }}
                        onError={(e) => {
                            if (onAudioError) {
                                onAudioError(getOriginalUrl(e.target.src));
                            }
                        }}
                        onLoadedMetadata={(e) => {
                            if (onAudioMetadata) {
                                onAudioMetadata(getOriginalUrl(e.target.src), e.target.duration);
                            }
                        }}
                    />
                )}

                {/* Top badge / progress dots removed — teacher and students should focus on content */}

                {state.sessionStarted &&
                    !showWelcome &&
                    !state.blanked &&
                    !photoSubSlide &&
                    !state.aiPaused &&
                    slide.mode !== "intro" &&
                    slide.mode !== "think_pause" &&
                    slide.mode !== "real_life_example" && (
                        (() => {
                            // Slide may still hint a preferred slot/size
                            // (declarative override). If present, we honour
                            // it; otherwise fall back to the runtime
                            // best-fit from `mascotPlacement`.
                            const frag =
                                slide.fragments?.[effectiveFragmentIndex] ||
                                {};
                            const declaredSlot =
                                frag.mascotSlot || slide.mascotSlot;
                            const declaredSize =
                                frag.mascotSize || slide.mascotSize;
                            const declaredDim =
                                declaredSize === "sm"
                                    ? 110
                                    : declaredSize === "lg"
                                      ? 360
                                      : declaredSize === "md"
                                        ? 220
                                        : null;
                            const dim = declaredDim ?? mascotPlacement.dim;
                            const pos = declaredSlot
                                ? computeSlotPos(declaredSlot, dim)
                                : {
                                      left: mascotPlacement.left,
                                      top: mascotPlacement.top,
                                  };
                            const resolvedSize =
                                dim === 110 ? "sm" : dim === 360 ? "lg" : "md";

                            // Smooth size + position transitions ── we
                            // render the Mascot at a fixed LARGE size
                            // (BASE = 360) and animate the OUTER motion.div
                            // via a single GPU `scale` transform. That way
                            // size and position interpolate together over
                            // the same duration, eliminating the
                            // size-snaps-first-then-glides effect.
                            //
                            // The fixed 360 box is centered on the same
                            // logical point as the resolved-size box, so
                            // visual bounds match the avoid-rect math.
                            const BASE = 360;
                            const targetScale = dim / BASE;
                            const boxLeft = pos.left + dim / 2 - BASE / 2;
                            const boxTop = pos.top + dim / 2 - BASE / 2;
                            return (
                                <motion.div
                                    initial={{
                                        opacity: 0,
                                        scale: targetScale * 0.85,
                                        x: boxLeft,
                                        y: boxTop,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        scale: targetScale,
                                        x: boxLeft,
                                        y: boxTop,
                                    }}
                                    /* All three transforms (x/y/scale)
                                       share the same duration so the
                                       avatar shrinks/grows as it slides
                                       to its new slot — finishing at the
                                       target size exactly when it
                                       arrives. */
                                    transition={{
                                        opacity: { duration: 0.6 },
                                        scale: {
                                            duration: 1.4,
                                            ease: [0.22, 1, 0.36, 1],
                                        },
                                        x: {
                                            duration: 1.4,
                                            ease: [0.22, 1, 0.36, 1],
                                        },
                                        y: {
                                            duration: 1.4,
                                            ease: [0.22, 1, 0.36, 1],
                                        },
                                    }}
                                    className="absolute z-20"
                                    style={{
                                        left: 0,
                                        top: 0,
                                        width: BASE,
                                        height: BASE,
                                        transformOrigin: "center center",
                                        willChange: "transform",
                                    }}
                                    data-testid="ai-mascot"
                                    data-mascot-size={resolvedSize}
                                >
                                    <Mascot state={mascotState} size="lg" />
                                </motion.div>
                            );
                        })()
                    )}

                {/* Caption — fixed bottom of 1920x1080 stage (above safe zone for annotations) */}
                {!photoSubSlide && (
                    <div className="absolute bottom-12 left-0 right-0 z-30 px-12">
                        <CaptionPill
                            text={captionText}
                            show={showCaption}
                            speaking={
                                mascotState === "speaking" ||
                                mascotState === "explaining"
                            }
                        />
                    </div>
                )}

                {/* Annotation overlay — covers full 1920x1080 stage.
                    Wrapper is pointer-events:none so underlying interactive
                    elements (drag-drop items) still receive clicks. The
                    inner canvas re-enables pointer events when a draw tool
                    is active. */}
                {state.sessionStarted && !state.blanked && (
                    <div
                        className="absolute inset-0 z-40"
                        style={{ pointerEvents: "none" }}
                    >
                        <AnnotationCanvas
                            strokes={slideStrokes}
                            interactive={annotationProps.interactive || false}
                            tool={annotationProps.tool || "off"}
                            color={annotationProps.color || "#2E8E9A"}
                            onAddStroke={annotationProps.onAddStroke}
                        />
                    </div>
                )}

                {/* Real-life example popup overlay — sits above the running slide */}
                <AnimatePresence>
                    {popupExample && (
                        <motion.div
                            key={popupExample.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.35 }}
                            className="absolute inset-0 z-50 flex items-center justify-center px-24"
                            style={{
                                backdropFilter: "blur(14px) saturate(120%)",
                                WebkitBackdropFilter: "blur(14px) saturate(120%)",
                                backgroundColor: "rgba(255,255,255,0.08)",
                                pointerEvents: "auto",
                            }}
                            data-testid="student-example-popup"
                        >
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0, y: 30 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.96, opacity: 0, y: 16 }}
                                transition={{
                                    duration: 0.5,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="relative w-full max-w-[1280px] rounded-[40px] bg-white soft-shadow overflow-hidden"
                            >
                                <div className="absolute top-6 left-6 z-10">
                                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFF1E5] text-[#C75730] text-xs uppercase tracking-[0.25em] font-semibold">
                                        Bonus · real-life example
                                    </span>
                                </div>
                                {onExamplePopupClose && (
                                    <button
                                        onClick={onExamplePopupClose}
                                        data-testid="student-example-popup-close"
                                        className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-[#14202a] flex items-center justify-center text-2xl font-light shadow"
                                    >
                                        ×
                                    </button>
                                )}
                                <div className="grid grid-cols-12 gap-10 items-center p-10">
                                    <div className="col-span-7">
                                        <img
                                            src={popupExample.image}
                                            alt={
                                                popupExample.headline ||
                                                popupExample.text
                                            }
                                            className="w-full aspect-[4/3] object-cover rounded-[28px]"
                                        />
                                    </div>
                                    <div className="col-span-5">
                                        <p className="text-sm uppercase tracking-[0.3em] text-[#4a5662] mb-4">
                                            Real-life example
                                        </p>
                                        <h2 className="text-5xl font-semibold tracking-tight leading-[1.05] mb-6">
                                            {popupExample.headline ||
                                                popupExample.text}
                                        </h2>
                                        <p className="text-2xl text-[#14202a] leading-relaxed">
                                            {popupExample.description ||
                                                popupExample.text}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </ScaledStage>
    );
};

export default LessonStage;
